import { tokens } from '@orbind/tokens'
import type { ReactNode } from 'react'
import { useState } from 'react'

/** Pecas pequenas compartilhadas do modulo de Itens de Trabalho. */

export const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

/** Cor solida para cada tone de catalogo (pill de status, dots, barras). */
export const TONE_COLOR: Record<string, string> = {
  neutral: tokens.color.textSubtle,
  info: tokens.color.info,
  primary: tokens.color.primary,
  success: tokens.color.success,
  warning: tokens.color.warning,
  danger: tokens.color.danger,
}

/** Fundo/texto suaves para a pill pequena de status das listas. */
export const TONE_SOFT: Record<string, { bg: string; fg: string }> = {
  neutral: { bg: tokens.color.background3, fg: tokens.color.textSubtle },
  info: { bg: tokens.color.primaryTint3, fg: tokens.color.info },
  primary: { bg: tokens.color.primaryTint, fg: tokens.color.primary },
  success: { bg: '#EAFAF3', fg: tokens.color.successDeep },
  warning: { bg: tokens.color.warningSurface, fg: tokens.color.warningDeep },
  danger: { bg: tokens.color.dangerSurface, fg: tokens.color.dangerDeep },
}

export function StatusPill({ tone, children }: { tone: string; children: ReactNode }) {
  const soft = TONE_SOFT[tone] ?? TONE_SOFT.neutral!
  return (
    <span className="wi-status-pill" style={{ background: soft.bg, color: soft.fg }}>
      {children}
    </span>
  )
}

/**
 * Cabecalho de secao recolhivel do detalhe: icone que vira chevron no hover
 * (comportamento .sec-tgl do prototipo).
 */
export function SectionHeader({
  icon,
  title,
  count,
  open,
  onToggle,
  right,
  info,
}: {
  icon: string
  title: string
  count?: ReactNode
  open: boolean
  onToggle: () => void
  right?: ReactNode
  /** O "i" de instrucoes do campo/secao, colado ao titulo (2026-09-01). */
  info?: ReactNode
}) {
  return (
    <div className="wi-sec__head">
      <button
        type="button"
        className="wi-sec__toggle"
        aria-label={`Recolher/expandir ${title.toLowerCase()}`}
        aria-expanded={open}
        title="Recolher/expandir"
        onClick={onToggle}
      >
        <i className={`fas fa-chevron-${open ? 'down' : 'right'} wi-sec__chev`} aria-hidden />
      </button>
      <i className={`${icon} wi-sec__ico`} aria-hidden />
      <h3 className="wi-sec__title">{title}</h3>
      {info}
      {count !== undefined ? <span className="wi-sec__count">{count}</span> : null}
      {right}
    </div>
  )
}

export function EmptyBlock({ icon, title, text, action }: { icon: string; title: string; text: string; action?: ReactNode }) {
  return (
    <div className="wi-empty">
      <div className="wi-empty__icon">
        <i className={icon} aria-hidden />
      </div>
      <span className="wi-empty__title">{title}</span>
      <span className="wi-empty__text">{text}</span>
      {action}
    </div>
  )
}

/** Dropdown ancorado com backdrop transparente (padrao dos menus do prototipo). */
export function Dropdown({
  open,
  onClose,
  children,
  align = 'right',
  width,
  className,
}: {
  open: boolean
  onClose: () => void
  children: ReactNode
  align?: 'left' | 'right'
  width?: number
  className?: string
}) {
  if (!open) return null
  return (
    <>
      <div className="wi-menu__backdrop" onClick={onClose} />
      <div
        className={`wi-menu${align === 'left' ? ' wi-menu--left' : ''}${className ? ` ${className}` : ''}`}
        style={width ? { width } : undefined}
        role="menu"
      >
        {children}
      </div>
    </>
  )
}

/** Dialogo "Tem certeza?" do prototipo (07-wi-confirm-close). */
export function ConfirmDanger({
  open,
  message,
  onCancel,
  onConfirm,
}: {
  open: boolean
  message: string
  onCancel: () => void
  onConfirm: () => void
}) {
  if (!open) return null
  return (
    <>
      <div className="wi-modal__backdrop" onClick={onCancel} />
      <div className="wi-modal wi-modal--name" role="alertdialog" aria-label="Confirmar remoção" style={{ width: 380 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'var(--ob-danger-surface)',
              color: 'var(--ob-danger)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            <i className="fas fa-exclamation-triangle" aria-hidden style={{ fontSize: 14 }} />
          </span>
          <div>
            <h2 className="wi-modal__title" style={{ margin: '0 0 4px' }}>
              Tem certeza?
            </h2>
            <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 13, color: 'var(--ob-text)', margin: 0, lineHeight: 1.5 }}>
              {message}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="wi-hbtn" style={{ fontWeight: 500 }} onClick={onCancel}>
            Cancelar
          </button>
          <button
            type="button"
            className="wi-hbtn"
            style={{ border: 'none', background: 'var(--ob-danger)', color: '#fff' }}
            onClick={onConfirm}
          >
            Confirmar
          </button>
        </div>
      </div>
    </>
  )
}

/** Estado de confirmacao destrutiva: guarda a acao pendente ate o Confirmar. */
export function useConfirm() {
  const [pending, setPending] = useState<{ message: string; run: () => void } | null>(null)
  return {
    ask: (message: string, run: () => void) => setPending({ message, run }),
    element: (
      <ConfirmDanger
        open={pending !== null}
        message={pending?.message ?? ''}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          pending?.run()
          setPending(null)
        }}
      />
    ),
  }
}

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const timeAgo = (date: string | Date) => {
  const diffMs = Date.now() - new Date(date).getTime()
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return 'agora'
  if (minutes < 60) return `há ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `há ${hours} h`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'ontem'
  if (days < 30) return `há ${days} dias`
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(date))
}

export const FILE_ICON: Record<string, string> = {
  'application/pdf': 'fas fa-file-pdf',
  'image/png': 'fas fa-file-image',
  'image/jpeg': 'fas fa-file-image',
  'text/plain': 'fas fa-file-alt',
}

export const fileIcon = (mime: string) =>
  FILE_ICON[mime] ??
  (mime.startsWith('image/')
    ? 'fas fa-file-image'
    : mime.includes('spreadsheet') || mime.includes('excel')
      ? 'fas fa-file-excel'
      : mime.includes('word')
        ? 'fas fa-file-word'
        : 'fas fa-file')

/**
 * Campo numérico com os botões de aumentar e diminuir (2026-08-31).
 *
 * Usado pela "Quantidade de dias" no painel de Datas e na barra de
 * propriedades — os dois lugares mostram o MESMO campo, então ele mora aqui.
 * `null` é o campo vazio (sem duração declarada), diferente de zero.
 */
export function NumberStepper({
  value,
  onChange,
  min = 0,
  max = 3650,
  disabled,
  label,
  suffix,
}: {
  value: number | null
  onChange: (v: number | null) => void
  min?: number
  max?: number
  disabled?: boolean
  label: string
  suffix?: string
}) {
  const limita = (n: number) => Math.min(max, Math.max(min, n))
  const passo = (d: number) => onChange(limita((value ?? 0) + d))
  return (
    <div className={`ob-stepper${disabled ? ' ob-stepper--off' : ''}`}>
      <button
        type="button"
        className="ob-stepper__btn"
        aria-label={`Diminuir ${label}`}
        title="Diminuir"
        disabled={disabled || (value ?? 0) <= min}
        onClick={() => passo(-1)}
      >
        <i className="fas fa-minus" aria-hidden />
      </button>
      <input
        className="ob-stepper__campo"
        inputMode="numeric"
        aria-label={label}
        placeholder="—"
        disabled={disabled}
        value={value == null ? '' : String(value)}
        onChange={(e) => {
          const limpo = e.target.value.replace(/\D/g, '').slice(0, 4)
          onChange(limpo === '' ? null : limita(Number(limpo)))
        }}
      />
      {suffix ? <span className="ob-stepper__suf">{suffix}</span> : null}
      <button
        type="button"
        className="ob-stepper__btn"
        aria-label={`Aumentar ${label}`}
        title="Aumentar"
        disabled={disabled || (value ?? 0) >= max}
        onClick={() => passo(1)}
      >
        <i className="fas fa-plus" aria-hidden />
      </button>
    </div>
  )
}
